import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2, Mail, MessageCircle, Bell, RotateCcw, FilterX } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { deliveryLogApi, type DeliveryLogFilters, type DeliveryLogRow } from './api';

const PAGE_SIZE = 50;

function ChannelIcon({ channel }: { channel: DeliveryLogRow['Channel'] }) {
  if (channel === 'Email') return <Mail size={14} className="text-blue-500" />;
  if (channel === 'WhatsApp') return <MessageCircle size={14} className="text-green-500" />;
  return <Bell size={14} className="text-gray-400" />;
}

export function DeliveryLogPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DeliveryLogFilters>({});
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const logQuery = useQuery({
    queryKey: ['delivery-log', filters, offset],
    queryFn: () => deliveryLogApi.list({ ...filters, limit: PAGE_SIZE, offset }),
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => deliveryLogApi.resend(id),
    onSuccess: () => {
      toast.success('Re-queued for delivery.');
      queryClient.invalidateQueries({ queryKey: ['delivery-log'] });
    },
    onError: () => toast.error('Resend failed.'),
  });

  const rows = logQuery.data?.data ?? [];
  const total = Number(logQuery.data?.meta?.total ?? 0);

  const setFilter = (key: keyof DeliveryLogFilters, value: string) => {
    setOffset(0);
    setFilters((f) => ({ ...f, [key]: value || undefined }));
  };

  const clearFilters = () => {
    setOffset(0);
    setFilters({});
  };

  const hasFilters = Object.values(filters).some(v => v !== undefined);

  return (
    <div className="space-y-4">
      {/* filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-40">
          <Select
            value={filters.channel ?? 'all'}
            onValueChange={(val) => setFilter('channel', val === 'all' ? '' : val)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="InApp">InApp</SelectItem>
              <SelectItem value="Email">Email</SelectItem>
              <SelectItem value="WhatsApp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-40">
          <Select
            value={filters.status ?? 'all'}
            onValueChange={(val) => setFilter('status', val === 'all' ? '' : val)}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Queued">Queued</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Failed">Failed</SelectItem>
              <SelectItem value="Skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Input
          placeholder="Recipient…"
          className="h-9 w-52"
          value={filters.recipient ?? ''}
          onChange={(e) => setFilter('recipient', e.target.value)}
        />

        <div className="flex items-center gap-2">
          <Input
            type="date"
            className="h-9 w-40"
            value={filters.dateFrom ?? ''}
            onChange={(e) => setFilter('dateFrom', e.target.value)}
          />
          <span className="text-gray-400">—</span>
          <Input
            type="date"
            className="h-9 w-40"
            value={filters.dateTo ?? ''}
            onChange={(e) => setFilter('dateTo', e.target.value)}
          />
        </div>

        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-gray-500 h-9"
          >
            <FilterX size={14} className="mr-2" />
            Clear
          </Button>
        )}
      </div>

      {/* table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="w-48 px-4">Queued</TableHead>
              <TableHead className="px-4">Event</TableHead>
              <TableHead className="px-4">Recipient</TableHead>
              <TableHead className="px-4">Channel</TableHead>
              <TableHead className="px-4">Status</TableHead>
              <TableHead className="w-24 px-4 text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-gray-400">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!logQuery.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-gray-400">
                  No delivery log entries match.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <React.Fragment key={r.DeliveryLogID}>
                <TableRow
                  onClick={() =>
                    setExpanded(expanded === r.DeliveryLogID ? null : r.DeliveryLogID)
                  }
                  className="cursor-pointer group"
                >
                  <TableCell className="px-4 py-3 text-gray-500">
                    {r.QueuedOn ? format(new Date(r.QueuedOn), 'MMM d, yyyy HH:mm') : '—'}
                  </TableCell>
                  <TableCell className="px-4 py-3 font-medium text-gray-900">
                    {r.EventType}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-gray-600 max-w-[220px] truncate">
                    {r.Recipient}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="flex items-center gap-2 text-gray-700">
                      <ChannelIcon channel={r.Channel} />
                      {r.Channel}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <StatusBadge status={r.Status} />
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right">
                    {r.Status === 'Failed' && (
                      <Button
                        variant="ghost_blue"
                        size="sm"
                        className="h-7 px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          resendMutation.mutate(r.DeliveryLogID);
                        }}
                      >
                        <RotateCcw size={12} className="mr-1" />
                        Resend
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {expanded === r.DeliveryLogID && (
                  <TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
                    <TableCell colSpan={6} className="px-8 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[13px] text-gray-600">
                        <div className="space-y-2">
                          {r.Subject && (
                            <p>
                              <span className="font-semibold text-gray-900">Subject:</span>{' '}
                              {r.Subject}
                            </p>
                          )}
                          {r.BodyPreview && (
                            <p>
                              <span className="font-semibold text-gray-900">Preview:</span>{' '}
                              <span className="italic">{r.BodyPreview}</span>
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <p>
                            <span className="font-semibold text-gray-900">Attempts:</span>{' '}
                            {r.AttemptCount}
                          </p>
                          {r.SentOn && (
                            <p>
                              <span className="font-semibold text-gray-900">Sent:</span>{' '}
                              {format(new Date(r.SentOn), 'MMM d, yyyy HH:mm:ss')}
                            </p>
                          )}
                          {r.ErrorMessage && (
                            <p className="text-red-600 bg-red-50 p-2 rounded-lg border border-red-100 mt-2">
                              <span className="font-semibold">Error:</span> {r.ErrorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* pager */}
      <div className="flex items-center justify-between mt-4 text-[13px] text-gray-500">
        <p>
          Showing <span className="font-medium text-gray-900">{rows.length ? offset + 1 : 0}</span> to{' '}
          <span className="font-medium text-gray-900">{offset + rows.length}</span> of{' '}
          <span className="font-medium text-gray-900">{total}</span> entries
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon_sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="rounded-lg"
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outline"
            size="icon_sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-lg"
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
